import React, { useState } from "react";
import { useAuth, useThemeMode, retroColors } from '@vortx/shared-ui';
import { useNavigate } from "react-router-dom";
import {
    Paper, TextField, Button, Typography, Box, Alert, CircularProgress
} from "@mui/material";
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { API_BASE_URL } from '../services/api';

export default function CompleteProfilePage() {
    const [name, setName] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const { currentUser, refreshUserProfile } = useAuth();
    const navigate = useNavigate();
    const { isRetro } = useThemeMode();

    async function handleSubmit(e) {
        e.preventDefault();
        setError("");

        if (!name.trim()) {
            setError("Please enter your name");
            return;
        }

        if (name.trim().length < 2) {
            setError("Name must be at least 2 characters");
            return;
        }

        setLoading(true);

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/profile`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: name.trim() })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to update profile');
            }

            // Refresh user profile to get updated data
            if (refreshUserProfile) {
                await refreshUserProfile();
            }

            navigate("/dashboard");
        } catch (err) {
            setError(err.message || "Failed to update profile");
        }

        setLoading(false);
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isRetro ? retroColors.bg : 'background.default',
            p: 3
        }}>
            <Paper sx={{
                p: 0,
                maxWidth: 480,
                width: '100%',
                bgcolor: isRetro ? retroColors.bg : 'background.paper',
                border: `1px solid ${vortxColors.gold}`,
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    background: isRetro ? 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)' : 'none',
                    p: 3,
                    borderBottom: `1px solid ${vortxColors.gold}`,
                    textAlign: 'center'
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: isRetro ? '#666' : 'text.secondary',
                        letterSpacing: 3,
                        textTransform: 'uppercase',
                        mb: 1
                    }}>
                        {/* system initialization */}
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '1.5rem',
                        color: vortxColors.gold,
                        letterSpacing: 4,
                        fontWeight: 400
                    }}>
                        VORTX FLUX
                    </Typography>
                </Box>

                {/* Content */}
                <Box sx={{ p: 4 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        color: vortxColors.gold,
                        fontSize: '0.75rem',
                        letterSpacing: 1,
                        mb: 2
                    }}>
                        &gt; COMPLETE_PROFILE
                    </Typography>

                    <Typography sx={{
                        color: isRetro ? '#fff' : 'text.primary',
                        fontSize: '1.1rem',
                        mb: 1
                    }}>
                        Almost there! Let's set up your profile.
                    </Typography>

                    <Typography sx={{
                        color: isRetro ? '#666' : 'text.secondary',
                        fontSize: '0.85rem',
                        mb: 3
                    }}>
                        Enter your name to complete your account setup.
                    </Typography>

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 2,
                                bgcolor: 'rgba(244, 67, 54, 0.1)',
                                color: '#f44336',
                                border: '1px solid rgba(244, 67, 54, 0.3)',
                                '& .MuiAlert-icon': { color: '#f44336' }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Name field */}
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                color: vortxColors.gold,
                                letterSpacing: 1,
                                mb: 0.5
                            }}>
                                FULL_NAME
                            </Typography>
                            <TextField
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                fullWidth
                                size="small"
                                placeholder="Enter your full name"
                                autoFocus
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: isRetro ? retroColors.surface : 'background.default',
                                        fontFamily: vortxTypography.mono,
                                        color: isRetro ? '#fff' : 'text.primary',
                                        '& fieldset': { borderColor: isRetro ? '#333' : 'divider' },
                                        '&:hover fieldset': { borderColor: vortxColors.gold },
                                        '&.Mui-focused fieldset': { borderColor: vortxColors.gold },
                                    },
                                    '& .MuiInputBase-input::placeholder': {
                                        color: isRetro ? '#555' : 'text.disabled',
                                        opacity: 1
                                    }
                                }}
                            />
                        </Box>

                        {/* Submit button */}
                        <Button
                            type="submit"
                            fullWidth
                            disabled={loading}
                            sx={{
                                bgcolor: vortxColors.gold,
                                color: isRetro ? '#0a0a0a' : vortxColors.green,
                                fontFamily: vortxTypography.mono,
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                letterSpacing: 2,
                                py: 1.5,
                                '&:hover': {
                                    bgcolor: '#c9a050'
                                },
                                '&.Mui-disabled': {
                                    bgcolor: isRetro ? '#555' : 'action.disabledBackground',
                                    color: isRetro ? '#888' : 'text.disabled'
                                }
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={20} sx={{ color: isRetro ? '#0a0a0a' : vortxColors.green }} />
                            ) : (
                                'CONTINUE'
                            )}
                        </Button>
                    </form>
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 2,
                    bgcolor: isRetro ? '#050505' : 'background.default',
                    borderTop: isRetro ? '1px solid #222' : '1px solid rgba(0,0,0,0.12)',
                    textAlign: 'center'
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: isRetro ? '#444' : 'text.secondary',
                        letterSpacing: 1
                    }}>
                        VORTX ENERGY
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}
